import { useBillableMaterials } from '../../api/queries/billableMaterials';
import { resolveBillableMaterials } from './ruleEngine';
import { AttributeChipData, EdgeSizeChipData } from '../Prediction/types';
import { getConfidence } from '../Prediction/utils';
import { useMemo } from 'react';
import { MaterialClassification } from '../../api/queries/materialClassification';

export function useResolvedBillableMaterials(
  organisationId: string,
  jobsiteId: string,
  materialClassification?: MaterialClassification,
  soilClassification?: AttributeChipData[],
  edgeEstimation?: EdgeSizeChipData[]
) {
  const { data: materials } = useBillableMaterials({ organisationId });

  const predictionData = useMemo(
    () => ({
      material: toMaterialPrediction(materialClassification),
      soil: toSoilPrediction(soilClassification),
      edges: toEdgePrediction(edgeEstimation),
    }),
    [materialClassification, soilClassification, edgeEstimation]
  );

  const jobsiteMaterials = useMemo(() => {
    return (
      materials?.filter((material) => material.jobsiteIds.length === 0 || material.jobsiteIds.includes(jobsiteId)) ?? []
    );
  }, [materials, jobsiteId]);

  const resolvedMaterials = useMemo(() => {
    if (!jobsiteMaterials.length) return [];

    return resolveBillableMaterials(jobsiteMaterials, predictionData);
  }, [jobsiteMaterials, predictionData]);

  return {
    resolvedMaterials,
  };
}

function toMaterialPrediction(materialClassification?: MaterialClassification) {
  if (!materialClassification || !materialClassification.mainChip) return undefined;

  return {
    className: materialClassification.mainChip.secondaryText.replace('material.class.', ''),
    probability: getConfidence(materialClassification.mainChip?.probability),
  };
}

function toSoilPrediction(
  soilClassification?: AttributeChipData[]
): Record<string, Record<string, number>> | undefined {
  if (!soilClassification) return undefined;

  return soilClassification.reduce(
    (acc, chip) => {
      const category = chip.name.replace(/attributes\.soilClassification\.categories\.|\.label/g, '');
      const value = chip.value.split('.').pop() ?? chip.value;
      const probability = getConfidence(chip.probability);

      acc[category] = {
        ...(acc[category] || {}),
        [value]: probability,
      };

      return acc;
    },
    {} as Record<string, Record<string, number>>
  );
}

function toEdgePrediction(edgeEstimation?: EdgeSizeChipData[]): number[][] | undefined {
  if (edgeEstimation?.length !== 1) return undefined;
  return edgeEstimation[0].edges.map(([x, y]) => [x, y]);
}
